import { useEffect, useState, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { PageTitle } from '../../../_metronic/layout/core';
import {
  StockTransfer,
  StockTransferStatusEnum,
  StockTransferList,
  listStockTransfers,
  receiveStockTransfer,
  deleteStockTransfer,
  uploadStockTransfersExcel,
  UploadStockTransfersModal,
} from '../../modules/stock-transfers';
import { authUserSelector } from '../../modules/auth';
import { Warehouse } from '../../modules/stock/models/StockLine';
import getWarehouseOptions from '../../modules/stock/services/getWarehouseOptions';

export function StockTransfersPage() {
  const intl = useIntl();
  const currentUser = useSelector(authUserSelector);
  const [stockTransfers, setStockTransfers] = useState<StockTransfer[]>([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<StockTransferStatusEnum | null>(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(
    null
  );
  const [showUploadModal, setShowUploadModal] = useState(false);

  const statusOptions = [
    {
      value: null,
      label: intl.formatMessage({ id: 'stockTransfers.anyStatus' }),
    },
    {
      value: StockTransferStatusEnum.PENDING,
      label: intl.formatMessage({ id: 'stockTransfers.status.pending' }),
    },
    {
      value: StockTransferStatusEnum.RECEIVED,
      label: intl.formatMessage({ id: 'stockTransfers.status.received' }),
    },
    {
      value: StockTransferStatusEnum.CANCELLED,
      label: intl.formatMessage({ id: 'stockTransfers.status.cancelled' }),
    },
  ];

  const warehouseOptions = getWarehouseOptions(intl);

  const fetchStockTransfers = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await listStockTransfers({
        status: status || undefined,
        warehouse: selectedWarehouse || undefined,
      });

      setStockTransfers(data);
    } catch (error) {
      console.warn('Error fetching stock transfers:', error);
      toast.error(intl.formatMessage({ id: 'stockTransfers.fetchError' }));
    } finally {
      setLoading(false);
    }
  }, [status, selectedWarehouse, intl]);

  useEffect(() => {
    fetchStockTransfers();
  }, [fetchStockTransfers]);

  const handleReceive = async (id: number) => {
    try {
      await receiveStockTransfer(id);
      toast.success(
        intl.formatMessage({ id: 'stockTransfers.receiveSuccess' })
      );
      fetchStockTransfers();
    } catch (error) {
      console.warn('Error receiving stock transfer:', error);
      toast.error(intl.formatMessage({ id: 'stockTransfers.receiveError' }));
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteStockTransfer(id);
      toast.success(intl.formatMessage({ id: 'stockTransfers.deleteSuccess' }));
      fetchStockTransfers();
    } catch (error) {
      console.warn('Error deleting stock transfer:', error);
      toast.error(intl.formatMessage({ id: 'stockTransfers.deleteError' }));
    }
  };

  const handleUploadExcel = async (file: File, warehouseId: number) => {
    try {
      await uploadStockTransfersExcel(file, warehouseId);
      toast.success(intl.formatMessage({ id: 'stockTransfers.uploadSuccess' }));
      fetchStockTransfers();
    } catch (error: any) {
      console.warn('Error uploading excel:', error);
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        intl.formatMessage({ id: 'stockTransfers.uploadError' });
      toast.error(errorMessage);
    }
  };

  return (
    <>
      <PageTitle breadcrumbs={[]}>
        {intl.formatMessage({ id: 'menu.stockTransfers' })}
      </PageTitle>

      <div className="card">
        <div className="card-header border-0 pt-6">
          <div className="card-toolbar w-100">
            <div className="w-100 d-flex align-items-center justify-content-between gap-3">
              <div className="d-flex flex-column">
                <label className="form-label fw-bold">
                  {intl.formatMessage({ id: 'stockTransfers.status' })}
                </label>
                <Select
                  options={statusOptions}
                  value={statusOptions.find((opt) => opt.value === status)}
                  onChange={(option) => {
                    setStatus(option?.value || null);
                  }}
                  placeholder={intl.formatMessage({
                    id: 'stockTransfers.filterByStatus',
                  })}
                  className="w-200px"
                />
              </div>
              <div className="d-flex flex-column">
                <label className="form-label fw-bold">
                  {intl.formatMessage({ id: 'stockTransfers.warehouse' })}
                </label>
                <Select
                  options={warehouseOptions}
                  value={warehouseOptions.find(
                    (opt) => opt.value === selectedWarehouse
                  )}
                  onChange={(option) => {
                    const value = option?.value;
                    setSelectedWarehouse(
                      value === '-' ? null : (value as Warehouse)
                    );
                  }}
                  placeholder={intl.formatMessage({
                    id: 'stockTransfers.filterByWarehouse',
                  })}
                  className="w-200px"
                />
              </div>
              <button
                type="button"
                className="d-block btn btn-primary ms-auto"
                onClick={() => setShowUploadModal(true)}
              >
                <i className="fa fa-upload me-2" />
                {intl.formatMessage({ id: 'stockTransfers.uploadExcel' })}
              </button>
            </div>
          </div>
        </div>

        <div className="card-body py-4">
          <StockTransferList
            items={stockTransfers}
            loading={loading}
            currentUserId={currentUser?.id}
            onReceive={handleReceive}
            onDelete={handleDelete}
          />
        </div>
      </div>

      <UploadStockTransfersModal
        visible={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onSubmit={handleUploadExcel}
      />
    </>
  );
}
